import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { MapPin, AlertTriangle, Heart, HandHeart, Truck, ArrowLeft, Languages } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import Countdown from "../components/Countdown";
import { PledgeAction } from "./AtRiskDashboard";
import { fmtTimeAgo, currency } from "../lib/format";
import { useI18n } from "../lib/i18n";
import { toast } from "sonner";

export default function DogProfile() {
    const { id } = useParams();
    const { lang } = useI18n();
    const [dog, setDog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [translated, setTranslated] = useState(null);
    const [translating, setTranslating] = useState(false);

    useEffect(() => {
        setLoading(true); setTranslated(null);
        api.get(`/dogs/${id}`).then((r) => { setDog(r.data); setLoading(false); })
            .catch(() => { setErr("Dog not found"); setLoading(false); });
    }, [id]);

    async function translate() {
        if (lang === "en") return toast.info("Switch to a non-English language first");
        setTranslating(true);
        try {
            const { data } = await api.post(`/dogs/${id}/translate`, { target_lang: lang });
            setTranslated(data);
            toast.success("Translated with Claude");
        } catch { toast.error("Translation failed"); }
        setTranslating(false);
    }

    if (loading) return <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 text-center text-[var(--rl-text-2)]">Loading…</div>;
    if (err || !dog) return <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 text-center">{err || "Not found"}</div>;

    const description = translated?.description || dog.description;
    const medNotes = translated?.medical_notes || dog.medical_notes;

    return (
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8" data-testid={`dog-profile-${dog.id}`}>
            <Link to="/dogs" className="inline-flex items-center gap-1.5 text-sm text-[var(--rl-text-2)] hover:text-[var(--rl-primary)] mb-6">
                <ArrowLeft className="w-4 h-4" /> Back to dogs
            </Link>

            {dog.is_at_risk && (
                <div className="mb-6 bg-[#fff5f3] border-l-4 border-[var(--rl-urgent)] p-4 flex flex-wrap items-center gap-4 rounded-r-lg" data-testid="risk-banner">
                    <AlertTriangle className="w-5 h-5 text-[var(--rl-urgent)]" />
                    <div className="flex-1 min-w-[200px]">
                        <div className="font-semibold text-[var(--rl-urgent)]">This dog is scheduled for euthanasia.</div>
                        <div className="text-sm text-[var(--rl-text-2)] mt-0.5">A single pledge, foster, or transport commitment can change everything.</div>
                    </div>
                    <Countdown isoDate={dog.euthanasia_date} size="md" />
                </div>
            )}

            <div className="grid lg:grid-cols-12 gap-10">
                <div className="lg:col-span-7">
                    <div className="aspect-[4/3] rounded-2xl overflow-hidden border border-[var(--rl-border)] bg-[var(--rl-cream)]">
                        <img src={dog.photo_url} alt={dog.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="mt-8">
                        <h1 className="font-serif text-5xl lg:text-6xl tracking-tight">{dog.name}</h1>
                        <div className="mt-2 text-[var(--rl-text-2)] text-lg">{dog.breed} · {dog.age} · {dog.gender} · {dog.size}</div>
                        <div className="flex items-center gap-1.5 text-sm text-[var(--rl-text-3)] mt-2">
                            <MapPin className="w-4 h-4" /> {dog.location} · {dog.shelter_name}
                        </div>
                        <p className="mt-6 text-[var(--rl-text)] leading-relaxed" data-testid="dog-description">{description}</p>
                        {lang !== "en" && (
                            <Button variant="outline" size="sm" onClick={translate} disabled={translating}
                                    className="mt-3 rounded-full border-[var(--rl-border-strong)] text-xs" data-testid="translate-btn">
                                <Languages className="w-3.5 h-3.5 mr-1.5" />
                                {translating ? "Translating…" : translated ? "Show original" : "Translate to my language"}
                            </Button>
                        )}
                        {translated && (
                            <button onClick={() => setTranslated(null)} className="ml-2 text-xs text-[var(--rl-text-3)] underline">Show original</button>
                        )}

                        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
                            <Stat label="Energy" value={dog.energy_level} />
                            <Stat label="Weight" value={dog.weight_lbs ? `${dog.weight_lbs} lbs` : "—"} />
                            <Stat label="Color" value={dog.color || "—"} />
                            <Stat label="Fee" value={dog.adoption_fee ? currency(dog.adoption_fee) : "—"} />
                        </div>

                        {dog.temperament?.length > 0 && (
                            <div className="mt-8">
                                <div className="eyebrow mb-3">Temperament</div>
                                <div className="flex flex-wrap gap-2">
                                    {dog.temperament.map((t) => <Badge key={t} className="rounded-full bg-[var(--rl-cream)] text-[var(--rl-text)] hover:bg-[var(--rl-sage)]">{t}</Badge>)}
                                </div>
                            </div>
                        )}

                        {dog.good_with?.length > 0 && (
                            <div className="mt-6">
                                <div className="eyebrow mb-3">Good with</div>
                                <div className="flex flex-wrap gap-2">
                                    {dog.good_with.map((t) => <Badge key={t} variant="outline" className="rounded-full border-[var(--rl-border-strong)]">{t}</Badge>)}
                                </div>
                            </div>
                        )}

                        {medNotes && (
                            <div className="mt-8 bg-[var(--rl-cream)] rounded-xl p-5 border border-[var(--rl-border)]">
                                <div className="eyebrow mb-2">Medical notes</div>
                                <p className="text-sm text-[var(--rl-text)]">{medNotes}</p>
                            </div>
                        )}
                    </div>
                </div>

                <aside className="lg:col-span-5 lg:sticky lg:top-24 self-start">
                    <div className="card-warm rounded-2xl p-6">
                        <div className="eyebrow mb-3">Take action</div>
                        <div className="grid gap-2.5">
                            <PledgeAction dog={dog} type="rescue" label="Make a Rescue Pledge" icon={<Heart className="w-4 h-4" />} primary />
                            <PledgeAction dog={dog} type="foster" label="Commit to Foster" icon={<HandHeart className="w-4 h-4" />} />
                            <PledgeAction dog={dog} type="transport" label="Offer Transport" icon={<Truck className="w-4 h-4" />} />
                            <PledgeAction dog={dog} type="donation" label="Donate to their fund" icon={<Heart className="w-4 h-4" />} />
                        </div>
                        <div className="mt-6 pt-6 border-t border-[var(--rl-border)]">
                            <div className="flex justify-between text-sm">
                                <span className="text-[var(--rl-text-2)]">Total pledged</span>
                                <span className="font-semibold">{currency(dog.total_pledged)}</span>
                            </div>
                            <div className="flex justify-between text-sm mt-1">
                                <span className="text-[var(--rl-text-2)]">Supporters</span>
                                <span className="font-semibold">{dog.pledge_count}</span>
                            </div>
                        </div>
                    </div>

                    {dog.pledges?.length > 0 && (
                        <div className="mt-6">
                            <div className="eyebrow mb-3">Recent pledges</div>
                            <div className="space-y-2">
                                {dog.pledges.slice(-5).reverse().map((p) => (
                                    <div key={p.id} className="card-warm rounded-lg p-3 flex items-start gap-3">
                                        <div className="w-8 h-8 rounded-full bg-[var(--rl-sage)] flex items-center justify-center text-xs font-semibold">{p.user_name[0]}</div>
                                        <div className="flex-1">
                                            <div className="text-sm"><span className="font-semibold">{p.user_name}</span> pledged <span className="text-[var(--rl-primary)]">{p.type}</span></div>
                                            {p.message && <div className="text-xs text-[var(--rl-text-2)] mt-1">"{p.message}"</div>}
                                            <div className="text-[10px] text-[var(--rl-text-3)] mt-1">{fmtTimeAgo(p.created_at)}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </aside>
            </div>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="border border-[var(--rl-border)] rounded-lg p-3">
            <div className="eyebrow">{label}</div>
            <div className="mt-1 font-serif text-lg capitalize">{value}</div>
        </div>
    );
}
